import React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Box, Flex, Icon, Link as ChakraLink, Text } from '@chakra-ui/react';
import {
  ArrowCircleUpIcon,
  ArrowLeftIcon,
  BeakerIcon,
  ColorSwatchIcon,
  HomeIcon,
  LightningBoltIcon,
  LockClosedIcon,
  PuzzleIcon,
  QuestionMarkCircleIcon,
  SupportIcon,
  TemplateIcon,
  TerminalIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/solid';
import { NextjsIcon } from '../icons/NextjsIcon';
import {
  RequestAuthIcon,
  RocketIcon,
  SignInIcon
} from '@components/common/icons';
import { DOCS_CATEGORIES, DOCS_URLS } from '@lib';
import { DocsNavigationContext } from '@context';
import { DocsCurrentViewOptions, DocsNavigation } from '@types';
import { SidenavItem } from './sidenav-item';
import { analytics, rudderTrack } from 'pages';

export const SIDENAV_WIDTH = '300px';

type DocsNavigationObject = {
  [key: string]: DocsNavigation;
};

type Props = {
  navigation: Array<DocsNavigation>;
  isMobileMenuOpen?: boolean;
  closeMobileMenu?: () => void;
};

function getCategory(slug: string) {
  return slug.split('/')[2] || 'home';
}

function getIconByCategory(category: string): React.FunctionComponent {
  switch (category) {
    case DOCS_CATEGORIES.home:
      return HomeIcon;
    case DOCS_CATEGORIES.nextjs:
      return NextjsIcon;
    case DOCS_CATEGORIES.quickstarts:
      return LightningBoltIcon;
    case DOCS_CATEGORIES.authentication:
      return SignInIcon;
    case DOCS_CATEGORIES.requestAuthentication:
      return RequestAuthIcon;
    case DOCS_CATEGORIES.users:
      return UserIcon;
    case DOCS_CATEGORIES.organizations:
      return UserGroupIcon;
    case DOCS_CATEGORIES.componentReference:
      return TemplateIcon;
    case DOCS_CATEGORIES.apiReference:
      return TerminalIcon;
    case DOCS_CATEGORIES.integrations:
      return PuzzleIcon;
    case DOCS_CATEGORIES.deployments:
      return RocketIcon;
    case DOCS_CATEGORIES.security:
      return LockClosedIcon;
    case DOCS_CATEGORIES.troubleshooting:
      return SupportIcon;
    case DOCS_CATEGORIES.upgrading:
      return ArrowCircleUpIcon;
    case DOCS_CATEGORIES.testing:
      return BeakerIcon;
    case DOCS_CATEGORIES.componentCustomization:
      return ColorSwatchIcon;
    default:
      return QuestionMarkCircleIcon;
  }
}

function getNavByCategory(navigation: Array<DocsNavigation>, category: string) {
  const navObject = navigation.reduce(
    (acc: DocsNavigationObject, cur: DocsNavigation) => {
      acc[cur.navId] = cur;
      return acc;
    },
    {}
  );

  // Mapping needed when category slug doesn't match navId from Sanity
  // (e.g. /docs/integration/:page and navId: "docs-integrations")
  switch (category) {
    case '':
      return navObject['docs-home'];
    case DOCS_CATEGORIES.requestAuthentication:
      return navObject['docs-request-auth'];
    case DOCS_CATEGORIES.apiReference:
      return navObject['docs-api-sdk'];
    case DOCS_CATEGORIES.integrations:
      return navObject['docs-integrations'];
    case DOCS_CATEGORIES.nextjs:
      return navObject['docs-nextjs'];
    default:
      return navObject[`docs-${category.toLowerCase()}`];
  }
}

export function NavigationContent({
  navigation,
  isMobileMenuOpen,
  closeMobileMenu = () => {
    /* noop */
  }
}: Props): JSX.Element {
  // TODO: Move this to the route's layout once Next releases it
  const { currentView, setCurrentView, resetCurrentView } = React.useContext(
    DocsNavigationContext
  );

  const { asPath } = useRouter();
  const activePath = asPath.replace(/#.*/, ''); // Remove hash fragment

  React.useEffect(() => {
    const isHome = activePath === '/docs';
    const shouldUpdateSidenav = currentView?.slug !== activePath;

    if (shouldUpdateSidenav) {
      if (isHome) {
        resetCurrentView();
      } else {
        const category = getCategory(activePath);
        const sideNav = getNavByCategory(navigation, category);

        if (!sideNav) return;

        const { name, categories } = sideNav;

        if (name === 'Docs Home') return;

        setCurrentView({
          view: 'children' as DocsCurrentViewOptions,
          name: name,
          slug: activePath,
          categories: categories,
          icon: getIconByCategory(category)
        });
      }
    }

    // Only check on page changes to avoid an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath]);

  const homeNavigation =
    navigation.find(({ navId }) => navId === 'docs-home')?.categories[0]
      .links || [];

  const handleCloseMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    }
  };

  const handleClick = (title: string) => {
    analytics.track(`Docs_Side Nav_Menu Item Clicked`, {
      surface: 'Docs',
      location: 'Side Nav',
      ClickedOn: title
    });
    rudderTrack(`Docs_Side Nav_Menu Item Clicked`, {
      surface: 'Docs',
      location: 'Side Nav',
      ClickedOn: title
    });
  };

  return (
    <Box
      as="nav"
      h={{ base: 'auto', lg: 'full' }}
      w="full"
      pos="relative"
      overflowY="auto"
      overflowX="hidden"
    >
      {/* Parents view */}
      <Box
        opacity={currentView?.view === 'parents' ? 1 : 0}
        position="absolute"
        top="0"
        transform={`translateX(${currentView?.view === 'parents' ? 0 : -100}%)`}
        transition="all .3s"
        zIndex={currentView?.view === 'parents' ? 1 : 0}
        w={{ base: 'full', sm: 'auto', lg: 'full' }}
        paddingRight={4}
        pb={{ base: 12, lg: 8 }}
      >
        {homeNavigation.map(({ _key, title, slug, url }) => {
          const icon = getIconByCategory(getCategory(slug));
          const href = url || slug;
          const isExternal = Boolean(url);

          return (
            <Link legacyBehavior key={_key} href={href} passHref>
              <ChakraLink
                isExternal={isExternal}
                onClick={handleCloseMobileMenu}
              >
                <SidenavItem
                  isActive={slug === activePath}
                  isExternal={isExternal}
                  icon={icon}
                  text={title}
                  handleClick={() => handleClick(title)}
                />
              </ChakraLink>
            </Link>
          );
        })}
      </Box>

      {/* Children view */}
      <Box
        opacity={currentView?.view === 'children' ? 1 : 0}
        position="absolute"
        top="0"
        transition="all .3s"
        transform={`translateX(${currentView?.view === 'children' ? 0 : 100}%)`}
        zIndex={currentView?.view === 'children' ? 1 : 0}
        w={{ base: 'full', sm: 'auto', lg: 'full' }}
        paddingRight={4}
        pb={{ base: 12, lg: 8 }}
      >
        <Link legacyBehavior href={DOCS_URLS.home} passHref>
          <a onClick={handleCloseMobileMenu}>
            <SidenavItem icon={ArrowLeftIcon} text="All docs" />
          </a>
        </Link>

        <Flex align="center" gap={3} px={3} py={5}>
          <Icon
            as={currentView?.icon as React.FunctionComponent}
            boxSize={6}
            color="var(--primaryTextColor)"
          />

          <Text
            color="var(--primaryTextColor)"
            textStyle={currentView?.name.length > 20 ? 'md' : 'lg-medium'}
          >
            {currentView?.name}
          </Text>
        </Flex>

        {currentView?.categories?.map(({ categoryId, categoryName, links }) => (
          <React.Fragment key={categoryId}>
            {categoryName ? (
              <Text
                color="var(--primaryTextColor)"
                textStyle="md-medium"
                pb={1}
                pt={4}
                pr={2}
                pl={12}
              >
                {categoryName}
              </Text>
            ) : null}

            {links.map(({ slug, url, title }) => {
              const href = url || slug;
              const isExternal = Boolean(url);

              if (!href) return null;

              return (
                <Link legacyBehavior key={href} href={href} passHref>
                  <ChakraLink
                    isExternal={isExternal}
                    onClick={handleCloseMobileMenu}
                  >
                    <SidenavItem
                      isActive={activePath === slug}
                      isExternal={isExternal}
                      text={title}
                      size="sm"
                      handleClick={() => handleClick(title)}
                    />
                  </ChakraLink>
                </Link>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
